import { useSelector } from "react-redux";
import { can } from "./can";

const resolvePermissionsPayload = (state) => {
  const payload = state?.permissions?.permissions;
  if (!payload || typeof payload !== "object") return {};
  if (payload.permissions && typeof payload.permissions === "object") {
    return payload.permissions;
  }
  return payload;
};

const resolveScopesPayload = (state) => {
  const payload = state?.permissions?.permissions;
  if (!payload || typeof payload !== "object") return [];
  return Array.isArray(payload.scopes) ? payload.scopes : [];
};

const resolveIsSuperUserPayload = (state) => {
  const payload = state?.permissions?.permissions;
  if (!payload || typeof payload !== "object") return false;
  return Boolean(payload.isSuperUser);
};

const unwrapNode = (raw) => {
  let source = raw;
  while (source && typeof source === "object") {
    if (source.defaultPrivileges && typeof source.defaultPrivileges === "object") {
      source = source.defaultPrivileges;
    } else if (source.permissions && typeof source.permissions === "object" && !source.permissions["*"]) {
      source = source.permissions;
    } else if (source.modules && typeof source.modules === "object") {
      source = source.modules;
    } else if (source.privileges && typeof source.privileges === "object") {
      source = source.privileges;
    } else {
      break;
    }
  }
  return source || {};
};

const derivePermissionFromKey = (permissionKey, rawPermissions) => {
  if (!permissionKey || typeof permissionKey !== "string") return null;

  const permissions = unwrapNode(rawPermissions);
  const normalized = permissionKey.toUpperCase();
  const parts = normalized.split("_").filter(Boolean);
  if (parts.length < 2) return null;

  const moduleKeys = Object.keys(permissions || {})
    .filter((key) => key && key !== "*")
    .sort((a, b) => b.length - a.length);

  const matchedModule = moduleKeys.find((moduleKey) => {
    const moduleParts = moduleKey.split("_").filter(Boolean);
    if (moduleParts.length >= parts.length) return false;
    return moduleParts.every((value, idx) => value === parts[idx]);
  });

  if (!matchedModule) return null;

  const modulePartLength = matchedModule.split("_").filter(Boolean).length;
  const remainingParts = parts.slice(modulePartLength);
  if (remainingParts.length < 2) return null;

  let moduleNode = permissions?.[matchedModule] || {};
  if (moduleNode?.subModules && typeof moduleNode.subModules === "object") {
    moduleNode = { ...moduleNode, ...moduleNode.subModules };
  }

  const subModuleKeys = Object.keys(moduleNode || {})
    .filter((key) => key && key !== "*" && key !== "all" && key !== "subModules")
    .sort((a, b) => b.length - a.length);

  const matchedSubModule = subModuleKeys.find((subModuleKey) => {
    const subModuleParts = subModuleKey.split("_").filter(Boolean);
    if (subModuleParts.length >= remainingParts.length) return false;
    return subModuleParts.every((value, idx) => value === remainingParts[idx]);
  });

  if (!matchedSubModule) {
    const fallbackAction = remainingParts[remainingParts.length - 1]?.toLowerCase();
    const fallbackSubModule = remainingParts.slice(0, -1).join("_");
    if (!fallbackSubModule || !fallbackAction) return null;
    return {
      module: matchedModule,
      subModule: fallbackSubModule,
      action: fallbackAction,
    };
  }

  const subModulePartLength = matchedSubModule.split("_").filter(Boolean).length;
  const action = remainingParts.slice(subModulePartLength).join("_").toLowerCase();
  if (!action) return null;

  return {
    module: matchedModule,
    subModule: matchedSubModule,
    action,
  };
};

export function usePermission() {
  const permissions = useSelector(resolvePermissionsPayload);
  const scopes = useSelector(resolveScopesPayload);
  const isSuperUserFromBackend = useSelector(resolveIsSuperUserPayload);
  const isSuper =
    isSuperUserFromBackend ||
    Boolean(permissions?.["*"]?.["*"]?.["*"]);

  return {
    can: (module, subModule, action) =>
      isSuper ? true : can(permissions, module, subModule, action),

    canByKey: (permissionKey) => {
      if (isSuper) return true;
      const resolved = derivePermissionFromKey(
        permissionKey,
        permissions
      );
      if (!resolved) return false;

      return can(
        permissions,
        resolved.module,
        resolved.subModule,
        resolved.action
      );
    },

    scopes,

    hasPolicyRole: (role) => {
      if (isSuper) return true;
      const targetRole = String(role || "").trim().toUpperCase();
      return scopes.some((s) => {
        const pRole = String(
          s.designation?.policyRole ||
            s.designation?.designationKey ||
            s.designation?.role ||
            s.policyRole ||
            s.designationKey ||
            ""
        )
          .trim()
          .toUpperCase();
        return pRole === targetRole;
      });
    },

    hasScopeLevel: (level) =>
      isSuper ? true : scopes.some((s) => s.scope?.level === level),

    isSuperAdmin: isSuper,
  };
}
